import * as React from 'react';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import './SignUp.css';

import { StepOne } from './signup-steps/StepOne';
import { StepTwo } from './signup-steps/StepTwo';
import { StepThree } from './signup-steps/StepThree';
import { StepFour } from './signup-steps/StepFour';
import { Logo } from './Logo';
import { QuoteMark } from './QuoteMark';
import { theme } from '../theme';
import ReactGA from 'react-ga4';
import { useSearchParams, useNavigate } from 'react-router-dom';

export default function SignUp({step = 1}) {
  const [currentStep, setCurrentStep] = React.useState(step);
  const [email, setEmail] = React.useState("");
  const [signupCode, setSignupCode] = React.useState("");
  const [messageId, setMessageId] = React.useState("");
  const [name, setName] = React.useState("");
  const [initializedThroughSSO, setInitializedThroughSSO] = React.useState(false);
  const [searchParams] = useSearchParams();
  const [provider, setProvider] = React.useState(null);
  const [requirePI, setRequirePI] = React.useState(true);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (currentStep === 1) {
      ReactGA.send({
        hitType: "pageview",
        path: "step-1"
      });
    }
    const email = searchParams.get('email');
    const name = searchParams.get('name');
    const signupVerificationCode = searchParams.get('signupCode');
    const provider = searchParams.get('provider');
    const requirePIStr = searchParams.get('requirePI');
    const requirePI = !requirePIStr || requirePIStr === 'true';
    if (email && signupVerificationCode && name && provider) {
      nextStepCallback(3, email, signupVerificationCode, null, name, true, provider, requirePI);
      navigate('/', { replace: true });
    }
  });

  const nextStepCallback = (step, email="", signupVerificationCode = "", messageId = "", name = "", initializedThroughSSO = false, provider = null, requirePI = true) => {
    ReactGA.send({
      hitType: "pageview",
      path: "step-"+step
    });
    setEmail(email);
    setName(name);
    setSignupCode(signupVerificationCode);
    setCurrentStep(step);
    setMessageId(messageId);    
    setInitializedThroughSSO(initializedThroughSSO);    
    setProvider(provider);
    setRequirePI(requirePI);
  };

  return (
    <ThemeProvider theme={theme}>
      {/* The intro, form and testimonial are separate grid areas, so phones get to the form before the testimonial */}
      <div className="ks-signup">
        <div className="ks-signup-panel" aria-hidden="true" />

        <header className="ks-signup-header">
          <a href="https://keystrike.com" className="ks-signup-logo" target="_blank" rel="noreferrer">
            <Logo />
          </a>
        </header>

        <div className="ks-signup-intro">
          <h1 className="ks-signup-h1">Better security can't wait</h1>
          <p className="ks-signup-sub">
            Sign up, register your organization, and join other forward-thinking and security aware organizations.
          </p>
        </div>

        <Box component="main" className="ks-signup-main">
          {currentStep === 1 ? <StepOne nextStepCallback={nextStepCallback} /> : null}
          {currentStep === 2 ? <StepTwo nextStepCallback={nextStepCallback} email={email} /> : null}
          {currentStep === 3 ? <StepThree nextStepCallback={nextStepCallback} email={email} signupCode={signupCode} fullName={name} initializedThroughSSO={initializedThroughSSO} provider={provider} requirePI={requirePI}/> : null}
          {currentStep === 4 ? <StepFour email={email} messageId={messageId} /> : null}
        </Box>

        {/* The quote style of the 2026 brand guidelines, with the avatar of its named testimonials */}
        <div className="ks-signup-quote">
          <figure className="ks-signup-quote-card">
            <blockquote className="ks-signup-quote-text">
              <p>
                In about 20 minutes, I had Keystrike up and running. The deployment is simple, well thought out, with clear documentation. Now Keystrike helps us establish that commands are genuine and trustworthy by detecting lurking attackers and blocking when they inject themselves into active sessions. With the combination of powerful technology and ease of deployment, I highly recommend testing Keystrike.
              </p>
            </blockquote>
            <figcaption className="ks-signup-quote-author">
              <span className="ks-signup-quote-avatar">
                <img
                  src="/testimonial-logo.png"
                  alt="Global Water Resources logo"
                />
              </span>
              <span className="ks-signup-quote-meta">
                <span className="ks-signup-quote-name">Steven Brill,</span>
                <br />
                VP of IT Operations and Security
                <br />
                Global Water Resources
              </span>
            </figcaption>
            <QuoteMark className="ks-signup-quote-mark" />
          </figure>
        </div>

        {/* Kept at its natural size and cropped as on keystrike.com, where the page starts 803px into the strip */}
        <div className="ks-signup-strip" aria-hidden="true">
          <img src="/gradient-strip.svg" alt="" width="2806" height="50" />
        </div>
      </div>
    </ThemeProvider>
  );
}
